import { SIGNATURE_MISMATCH_ERROR } from 'modules/common/constants/consent';
import { Member } from 'modules/common/interfaces/member';

export const getCookie = (name?: unknown) => {
    if (!name || typeof name !== 'string' || name.trim() === '') {
        return;
    }
    /* eslint-disable no-useless-escape */
    const escaped = name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1');
    const pattern = `(?:^|; )${escaped}=([^;]*)`;
    const regex = new RegExp(pattern);
    const matches = document.cookie.match(regex);

    if (!matches) {
        return;
    }

    return decodeURIComponent(matches[1]);
};

export const setCookie = (name: string, value: string, expireDate: Date, domain?: string) => {
    const domainStr = domain ? `; domain=${domain}` : '';
    const expireStr = expireDate ? `; expires=${expireDate.toUTCString()}` : '';
    document.cookie = `${name}=${encodeURIComponent(value || '')}${expireStr}${domainStr}; samesite=Lax; path=/`;
};

export const getInitials = (fullname = '') => {
    return fullname
        .split(' ')
        .map((item) => item.toUpperCase().slice(0, 1))
        .join('');
};

/**
 * Composes a member's legal name, mirroring the backend's fallback order
 * (`_signature_matches_member_names`): legal_* fields first, then first/last,
 * then fullname. Returns an empty string when no name is available.
 */
export const getMemberLegalName = (member?: Member): string => {
    const first = member?.legal_first_name || member?.first_name || '';
    const last = member?.legal_last_name || member?.last_name || '';
    const middle = member?.legal_middle_name || '';

    if (first && last) {
        return [first, middle, last].filter(Boolean).join(' ');
    }

    return member?.fullname?.trim() || '';
};

/**
 * The inline error shown when the backend rejects a signature as not matching the member's legal
 * name. Names the expected name when we know it, so the member can see what to type rather than
 * guessing; falls back to the bare sentence when no name is on file.
 */
export const getSignatureMismatchError = (member?: Member): string => {
    const legalName = getMemberLegalName(member);
    return legalName ? `${SIGNATURE_MISMATCH_ERROR} (${legalName}).` : `${SIGNATURE_MISMATCH_ERROR}.`;
};

// Regex is safe: input is always a bounded number-to-string conversion
export const formatNumberWithCommas = (number: number): string => {
    const fixed = number.toFixed(2);
    return fixed.endsWith('.00')
        ? fixed.slice(0, -3).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        : fixed.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * Formats the given value into a currency with whole numbers
 */
export const formatCurrency = (value: number | null | undefined): string => {
    if (!Number.isFinite(value)) {
        return '';
    }
    try {
        const formatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
        });
        return formatter.format(value);
    } catch (e) {
        // Fallback for environments without Intl support
        // Regex is safe: input is always a bounded number-to-string conversion
        console.warn('formatCurrency: Intl.NumberFormat failed, using fallback', e);
        return `$${Math.round(value)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
    }
};
